import NotesQuestionBubble from "@/Shared/Locations/Bubbles/NotesQuestionBubble";
import { isAmazonDistributionWarehouseBrand } from "@freightsimple/shared/Helpers/isAmazonDistributionWarehouseBrand";
import { AmazonReferenceIdQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/AmazonReferenceIdQuestionBubble";
import { AmazonShipmentIdQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/AmazonShipmentIdQuestionBubble";
import { BoothNumberQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/BoothNumberQuestionBubble";
import { ReferenceNumberQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/ReferenceNumberQuestionBubble";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useState } from "react";
import { ReferenceNumbersInput } from "@/Shared/ReferenceNumbers/Inputs/ReferenceNumbersInput";
import { useReferenceNumbersValidate } from "@/Shared/ReferenceNumbers/Hooks/useReferenceNumbersValidate";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";

interface ModifyReferenceAndNotesDialogBodyProps {
  shipmentId: string;
  referenceNumber: string;
  // Unpacked server-side from the reference for Amazon warehouse deliveries
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  notes: string;
  onDone: (result: ReferenceNumberAndNotes) => void;
  onCancel: () => void;
  locationContext: LocationContext | undefined;
  locationType: LocationType;
  distributionWarehouseBrand?: string | undefined;
}

export function ModifyReferenceAndNotesDialogBody(
  props: ModifyReferenceAndNotesDialogBodyProps,
) {
  const { onCancel, locationContext } = props;
  const [referenceNumber, setReferenceNumber] = useState(props.referenceNumber);
  const [notes, setNotes] = useState(props.notes);
  const [boothNumber, setBoothNumber] = useState(props.boothNumber);

  const [amazonReferenceId, setAmazonReferenceId] = useState(
    props.amazonReferenceId ?? "",
  );
  const [amazonShipmentId, setAmazonShipmentId] = useState(
    props.amazonShipmentId ?? "",
  );
  const [forceValidation, setForceValidation] = useState(false);

  // A stored reference the server could not unpack (a legacy free-form value)
  // keeps the plain input: forcing the two id fields onto it would hide the
  // value and block a notes-only edit behind ids the customer may not have.
  const storedReferenceIsLegacyFreeForm =
    props.referenceNumber.trim() !== "" &&
    props.amazonReferenceId === undefined &&
    props.amazonShipmentId === undefined;

  // Mirrors the server-side gate exactly: locationType AND brand
  const isAmazonDelivery =
    props.locationContext === LocationContext.Delivery &&
    props.locationType === LocationType.DistributionWarehouse &&
    isAmazonDistributionWarehouseBrand(props.distributionWarehouseBrand) &&
    !storedReferenceIsLegacyFreeForm;

  const validation = useReferenceNumbersValidate({
    locationContext: props.locationContext,
    originalReferenceNumber: props.referenceNumber,
    shipmentId: props.shipmentId,
  });

  function amazonReferenceIdError(): string | undefined {
    if (!amazonReferenceId || amazonReferenceId.trim() === "") {
      return "Amazon Reference ID is required";
    }
    if (amazonReferenceId.includes(",")) {
      return "Amazon Reference ID must not contain commas";
    }
    return undefined;
  }

  function amazonShipmentIdError(): string | undefined {
    if (!amazonShipmentId || amazonShipmentId.trim() === "") {
      return "Amazon Shipment ID is required";
    }
    if (amazonShipmentId.includes(",")) {
      return "Amazon Shipment ID must not contain commas";
    }
    return undefined;
  }

  function onDone() {
    if (isAmazonDelivery) {
      if (
        amazonReferenceIdError() !== undefined ||
        amazonShipmentIdError() !== undefined
      ) {
        setForceValidation(true);
        return;
      }
      // The server owns packing the ids into the carrier-facing reference
      props.onDone({
        referenceNumber: "",
        amazonReferenceId,
        amazonShipmentId,
        boothNumber,
        notes,
      });
      return;
    }

    props.onDone({
      referenceNumber,
      amazonReferenceId: undefined,
      amazonShipmentId: undefined,
      boothNumber,
      notes,
    });
  }

  const displayName =
    locationContext === LocationContext.Pickup ? "Pickup" : "Delivery";
  return (
    // The hosting Dialog sets the width; the form fills it.
    <Stack gap={4}>
      <Stack gap={0}>
        {props.locationContext === LocationContext.Pickup && (
          <T size="sm" color="secondary">
            Please include any notes or reference numbers that would help the
            driver complete this pickup
          </T>
        )}
        {props.locationContext === LocationContext.Delivery && (
          <T size="sm" color="secondary">
            Please include any notes or reference numbers that would help the
            driver complete this delivery
          </T>
        )}
      </Stack>

      {isAmazonDelivery ? (
        <Stack gap={4}>
          <FormItem
            label={
              <>
                Amazon Reference ID
                <AmazonReferenceIdQuestionBubble
                  displayName={displayName}
                  small
                />
              </>
            }
            error={forceValidation ? amazonReferenceIdError() : undefined}
            fillWidth
          >
            <TextInput
              type="text"
              width="full"
              value={amazonReferenceId}
              onChange={(e) => setAmazonReferenceId(e.target.value)}
              maxLength={30}
              data-testid="reference-dialog-amazon-reference-id-input"
            />
          </FormItem>
          <FormItem
            label={
              <>
                Amazon Shipment ID
                <AmazonShipmentIdQuestionBubble
                  displayName={displayName}
                  small
                />
              </>
            }
            error={forceValidation ? amazonShipmentIdError() : undefined}
            fillWidth
          >
            <TextInput
              type="text"
              width="full"
              value={amazonShipmentId}
              onChange={(e) => setAmazonShipmentId(e.target.value)}
              maxLength={30}
              data-testid="reference-dialog-amazon-shipment-id-input"
            />
          </FormItem>
        </Stack>
      ) : (
        <ReferenceNumbersInput
          validation={validation}
          label={
            <>
              {displayName} Reference Number
              <ReferenceNumberQuestionBubble displayName={displayName} small />
            </>
          }
          width="full"
          value={referenceNumber}
          onChange={(newValue: string) => setReferenceNumber(newValue)}
          data-testid="reference-dialog-reference-number-input"
        />
      )}

      {props.locationType === LocationType.TradeShow && (
        <FormItem
          label={
            <>
              {displayName} Booth Number
              <BoothNumberQuestionBubble displayName={displayName} small />
            </>
          }
          fillWidth
        >
          <TextInput
            type="text"
            width="full"
            value={boothNumber}
            onChange={(e) => setBoothNumber(e.target.value)}
          />
        </FormItem>
      )}

      <FormItem
        label={
          <>
            Notes
            <NotesQuestionBubble displayName={displayName} />
          </>
        }
        fillWidth
      >
        <TextInput
          placeholder="eg. Blue dock door, or ring bell"
          type="text"
          width="full"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormItem>

      <Row gap={2} justify="between">
        <Button onClick={onCancel}>Cancel</Button>

        <Button
          primary
          onClick={onDone}
          data-testid="reference-dialog-done-button"
        >
          Done
        </Button>
      </Row>
    </Stack>
  );
}

interface ReferenceNumberAndNotes {
  referenceNumber: string;
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  notes: string;
}
